import { formatoNumero } from '@/lib/utils/formato-precio';
import { formatoPrecio, pisoEnTexto, techoEnTexto } from '../components/barra-de-rangos';
import { porQueDeLaFila } from './fila-en-palabras';
import { noRige } from './jerarquia';
import type {
  ClaveDeSalida,
  ComoPide,
  Cruce,
  FilaSimulada,
  RangoDeRegla,
  RangoPisado,
  RedondeoAplicado,
  SalidasSinPrecio,
} from '../types/pricing-rules.types';

/**
 * La explicación única de un precio (spec 225, pieza 1; revierte el ruling H8
 * del plan 3). Todas las pantallas que dicen «por qué este precio» arman un
 * `RecorridoDelPrecio` con su adaptador y lo dibuja `PorQueEstePrecio`
 * (`components/por-que-este-precio.tsx`). Los adaptadores:
 *
 * - `desdeFilaSimulada` (acá): la foto, `/explicar` y `/simular`.
 * - `desdeSimulacion` y `desdeFilaDeLaBandeja` (`smart-pricing/lib/explicacion-del-precio.ts`):
 *   Análisis y las propuestas (Por decidir, el historial).
 *
 * Spec 227: lo que se lee primero (el título, el resultado, el porqué, los
 * avisos, las opciones) sale de `lectura-del-precio.ts`; el dibujo, de
 * `dibujo-del-precio.ts`; la cuenta paso a paso, de acá. Todo en funciones
 * puras y testeadas; el componente sólo dibuja. Nada se recalcula: el precio
 * lo decidió el backend.
 * Ausente es «no sé»: `null`, y la pantalla lo dice.
 */

export type Tiempo = 'HOY' | 'SI_CORRIERA' | 'PASO';

/**
 * Así llega la regla en borrador en `rangos` (backend: `ID_BORRADOR`). Al EDITAR una
 * regla (`reemplaza`) el backend le deja su id real: quien llama pasa `idBorrador`.
 * Vive acá (y no en `desglose-simulado.tsx`, que la re-exporta) para que los casos
 * de test la usen sin importar un componente.
 */
export const ID_BORRADOR = '__borrador__';

/** Una regla de tramo con su papel en ESTE precio. */
export type ReglaDelRecorrido = RangoDeRegla & {
  /** Está en `limitantes`: es la que deja el precio donde queda (MANDA). */
  manda: boolean;
  /** Es la que se prueba en «¿Y si…?» o en Nueva regla. */
  probando: boolean;
};

/** Lo que pide la estrategia (o de dónde se parte, si no hay). */
export interface PedidoDelRecorrido {
  /** ESTRATEGIA: la pide un método. HOY: sin estrategia, se parte del precio de hoy. VUELTA: plan 2. */
  origen: 'ESTRATEGIA' | 'HOY' | 'VUELTA';
  /** El nombre propio de la estrategia, o el método en llano (Análisis). */
  estrategia: string | null;
  como: ComoPide | null;
  usaMargenPropio: boolean;
  /** Plan 2: por qué vuelve a ese precio, congelado. */
  explicacionDeVuelta: string | null;
  /** null = no se sabe (propuesta vieja sin `strategyPrice`). */
  precio: number | null;
}

/** «¿Y si…?» y Nueva regla: el precio sin y con la regla que se prueba. */
export interface ComparacionDelRecorrido {
  /** Cómo se nombra la regla que se prueba: «la que probás», «esta regla». */
  etiqueta: string;
  /** Su nombre, para el título. */
  regla: string;
  sin: number | null;
  /** null = con ella no hay precio posible. */
  con: number | null;
}

/**
 * El mercado de este precio (spec 225, pieza 2). Lo llena la propuesta, que lo
 * congela al calcularse (lote B1); las demás fuentes (la foto, Análisis) lo
 * dejan en null y la explicación no lo dibuja.
 */
export interface MercadoDelRecorrido {
  /** Las tiendas que cuentan, de la más barata a la más cara. */
  tiendas: Array<{
    nombre: string;
    precio: number;
    leidoEn: string | null;
    /** 26-oct: la página del producto en esa tienda (la resuelve el servidor al leer). */
    url?: string | null;
  }>;
  /** Tu tienda, si no cuenta para el análisis. */
  propia: { nombre: string; precio: number; url?: string | null } | null;
  /** La propuesta es de antes y no lo guardó: es el de hoy (la fila abierta lo pide aparte). */
  deHoy?: boolean;
  /**
   * Cuándo se congeló (ISO). En una propuesta resuelta (`tiempo` PASO) «hace
   * cuánto se leyó» se mide contra esta fecha, no contra hoy (auditoría del 3-oct).
   */
  congeladoEn?: string;
}

export interface RecorridoDelPrecio {
  /**
   * CALCULADO: hay un precio. SIN_PRECIO_POSIBLE: las reglas no se cumplen
   * juntas. SIN_CALCULO: sin datos o sin estrategia: sólo el porqué.
   */
  estado: 'CALCULADO' | 'SIN_PRECIO_POSIBLE' | 'SIN_CALCULO';
  /** SIN_CALCULO: el porqué en una frase. */
  porQue: string | null;
  hoy: number | null;
  costo: number | null;
  pedido: PedidoDelRecorrido | null;
  /** null = no se guardó qué deja cada regla (propuesta de antes de los rangos). */
  reglas: ReglaDelRecorrido[] | null;
  /**
   * Lote B2: los límites que alcanzan pero no rigen (uno más específico del
   * mismo tipo los pisa). Ausente o vacío: ninguno, o no se sabe (lo de antes).
   */
  pisadas?: RangoPisado[];
  cruce: Cruce | null;
  /** Sólo cuando `reglas` es null: la regla que lo limitó, como se guardó. */
  reglaQueLimito: string | null;
  /** null = no movió el precio (o no se sabe: ver `notas`). */
  redondeo: (RedondeoAplicado & { probando: boolean }) | null;
  /** El precio al que lleva la cuenta. null sin precio posible o sin cálculo. */
  queda: number | null;
  /**
   * La foto: mientras nadie decide se sirve otro precio. Defensivo: hoy el
   * backend no lo produce en CALCULADO (`precioFinal` y `precioPropuesto` sólo
   * difieren sin precio posible, `decidirLaCorrida`).
   */
  seSirve: number | null;
  salidas: SalidasSinPrecio | null;
  /**
   * Una propuesta sin precio posible ya resuelta: la salida que eligió la
   * persona y su precio congelado (`salidaElegida` + `salidas`). null en el resto.
   */
  elegida: { clave: ClaveDeSalida; precio: number } | null;
  comparacion: ComparacionDelRecorrido | null;
  /** Lo que no se sabe y se dice («no guardó el redondeo»). Lo escribe el adaptador. */
  notas: string[];
  /** Plan 2 (pieza 2). */
  mercado: MercadoDelRecorrido | null;
  /**
   * El precio del más barato (spec 227 §2): de `comoPide` o, si no, del
   * `precioMasBarato` de la foto o de la propuesta. null = no se sabe, y la
   * explicación no lo nombra. Su tienda llega con el plan 2 (el mercado).
   */
  masBarato: number | null;
  /**
   * El nombre del producto, para «El Colchón no se movería solo» (spec 227, R1).
   * null = la fuente no lo trae (Análisis): la frase va sin sujeto.
   */
  producto: string | null;
}

/**
 * Cómo se llama el precio del que se parte: «hoy», salvo en el historial, donde
 * es el de antes de la propuesta (auditoría del 28-sep, C2).
 */
export const REFERENCIA: Record<Tiempo, string> = {
  HOY: 'hoy',
  SI_CORRIERA: 'hoy',
  PASO: 'antes',
};

/** El verbo de la fila final y del rótulo del eje. */
export const VERBOS: Record<Tiempo, string> = {
  HOY: 'Queda',
  SI_CORRIERA: 'Quedaría',
  PASO: 'Quedó',
};

export const NOTA_SIN_REDONDEO_FILA = 'Este cálculo es de antes: no guardó el redondeo.';
export const NOTA_SIN_REDONDEO_PROPUESTA = 'Esta propuesta es de antes: no guardó el redondeo.';
export const NOTA_SIN_RANGOS_PROPUESTA =
  'Esta propuesta es de antes: no guardó qué deja cada regla.';

/** El tramo de un eje que ocupan dos rótulos antes de pisarse (spec 225: 16 %). */
export const DISTANCIA_ENTRE_ROTULOS = 0.16;

const CASI = 0.005;
const fmt = (n: number | null | undefined) => (n == null ? '—' : formatoPrecio(n));
const pct = (n: number) => `${formatoNumero(n)} %`;
const redondo = (n: number) => Math.round(n * 100) / 100;

/** Margen sobre el precio, como el resto de Prixia: (precio − costo) ÷ precio. Dos decimales. */
export function margenDe(precio: number | null, costo: number | null): number | null {
  if (precio == null || costo == null || !(precio > 0)) return null;
  return Math.round(((precio - costo) / precio) * 10000) / 100;
}

/** Una regla que pone un tramo medible (no la que no pudo medir). */
export const esDeTramo = (r: RangoDeRegla) => r.aviso == null && (r.min != null || r.max != null);

/**
 * Sin precio posible, cuántas reglas no se pueden cumplir juntas: «ningún precio
 * cumple las dos» sólo si son dos (informe 229, textos); con más, «todas». Una
 * regla con dos límites cuenta una vez.
 */
export const cualesCumplir = (reglas: RangoDeRegla[] | null | undefined): 'las dos' | 'todas' =>
  new Set((reglas ?? []).filter(esDeTramo).map((r) => r.ruleId)).size > 2 ? 'todas' : 'las dos';

/** Un precio más barato de 0 (o menos) no es un dato: ausente, nunca inventado. */
export const masBaratoPositivo = (n: unknown): number | null =>
  typeof n === 'number' && n > 0 ? n : null;

/** El más barato que usó el método, si lo usó (igualar, quedar por debajo). */
export function masBaratoDe(c: ComoPide | null | undefined): number | null {
  return c?.metodo === 'MATCH_LOWEST' || c?.metodo === 'UNDERCUT_PERCENT' ? c.masBarato : null;
}

/**
 * Una regla con dos límites (dos acciones) trae dos rangos con el mismo `ruleId`,
 * y `limitantes` sólo dice el id. Manda el rango cuyo borde es el precio que dejan
 * las reglas (antes del redondeo); si ninguno lo toca, el primero. Revisión final
 * del plan 1 (28-sep).
 */
export function mandaEnElRango(
  r: RangoDeRegla,
  rangos: RangoDeRegla[],
  limitantes: string[],
  precioDeLasReglas: number | null
): boolean {
  if (!limitantes.includes(r.ruleId)) return false;
  const delMismo = rangos.filter((x) => x.ruleId === r.ruleId);
  if (delMismo.length <= 1) return true;
  const toca = (x: RangoDeRegla) =>
    precioDeLasReglas != null &&
    ((x.min != null && Math.abs(x.min - precioDeLasReglas) <= CASI) ||
      (x.max != null && Math.abs(x.max - precioDeLasReglas) <= CASI));
  if (precioDeLasReglas != null && delMismo.some(toca)) return toca(r);
  return delMismo[0] === r;
}

/**
 * Una `FilaSimulada` (la foto, `/explicar`, `/simular`). `borrador`: la regla
 * que se prueba («¿Y si…?», Nueva regla); sin él es la foto de hoy. `costo`
 * pisa el de la fila (la foto lo trae aparte).
 */
export function desdeFilaSimulada(
  f: FilaSimulada,
  opts: {
    /**
     * `nombre`: el de la regla que se prueba, como la llama la pantalla. Se usa si
     * el backend no la trae en `rangos` (no le pone límite a este producto), así
     * el título no pasa de su nombre a «la regla nueva» al terminar de simular
     * (informe 229, textos).
     */
    borrador?: { etiqueta: string; id: string; nombre?: string } | null;
    costo?: number | null;
  } = {}
): RecorridoDelPrecio {
  const b = opts.borrador ?? null;
  const costo = opts.costo !== undefined ? opts.costo : (f.costo ?? null);
  const estado =
    f.estado === 'SIN_DATOS' || f.estado === 'SIN_ESTRATEGIA'
      ? 'SIN_CALCULO'
      : f.estado === 'SIN_PRECIO_POSIBLE'
        ? 'SIN_PRECIO_POSIBLE'
        : 'CALCULADO';
  // Lo que dejan las reglas antes de redondear (el mismo valor que usa la
  // cuenta, `cuentaDelPrecio`, para saber cuál rango manda).
  const precioDeLasReglas =
    estado === 'CALCULADO' ? (f.redondeo ? f.redondeo.desde : f.precioPropuesto) : null;
  const reglas = f.rangos.map((r) => ({
    ...r,
    manda: mandaEnElRango(r, f.rangos, f.limitantes, precioDeLasReglas),
    probando: b != null && r.ruleId === b.id,
  }));
  // El redondeo sólo nombra al borrador si ES el borrador (auditoría C1): otro
  // redondeo más puntual puede ganarle, y el título nombraría otra regla.
  const nombreDelBorrador =
    (b &&
      (f.rangos.find((r) => r.ruleId === b.id)?.ruleName ??
        (f.redondeo?.ruleId === b.id ? f.redondeo.ruleName : undefined))) ||
    b?.nombre ||
    'la regla nueva';
  return {
    estado,
    porQue: estado === 'SIN_CALCULO' ? porQueDeLaFila(f) : null,
    hoy: f.precioHoy,
    costo,
    pedido:
      f.origenDelPedido === 'HOY'
        ? {
            origen: 'HOY',
            estrategia: null,
            como: null,
            usaMargenPropio: false,
            explicacionDeVuelta: null,
            precio: f.precioHoy,
          }
        : {
            origen: 'ESTRATEGIA',
            estrategia: f.estrategia?.name ?? null,
            como: f.comoPide ?? null,
            usaMargenPropio: f.usaMargenPropio,
            explicacionDeVuelta: null,
            precio: f.precioPedido,
          },
    reglas,
    pisadas: f.pisados ?? [],
    cruce: f.cruce,
    reglaQueLimito: null,
    redondeo:
      estado === 'CALCULADO' && f.redondeo
        ? { ...f.redondeo, probando: b != null && f.redondeo.ruleId === b.id }
        : null,
    queda: estado === 'CALCULADO' ? f.precioPropuesto : null,
    seSirve:
      estado === 'CALCULADO' && !b && Math.abs(f.precioFinal - f.precioPropuesto) >= CASI
        ? f.precioFinal
        : null,
    salidas: estado === 'SIN_PRECIO_POSIBLE' ? f.salidas : null,
    elegida: null,
    comparacion: b
      ? {
          etiqueta: b.etiqueta,
          regla: nombreDelBorrador,
          sin: f.precioSinBorrador,
          con: estado === 'CALCULADO' ? f.precioPropuesto : null,
        }
      : null,
    // Ausente = no se sabe (una fila de la foto guardada antes del 28-sep).
    notas: estado === 'CALCULADO' && f.redondeo === undefined ? [NOTA_SIN_REDONDEO_FILA] : [],
    mercado: null,
    // La foto trae `precioMasBarato` (`FilaDeLaFoto`); `/explicar` y `/simular`, no.
    masBarato:
      masBaratoPositivo(masBaratoDe(f.comoPide)) ??
      ('precioMasBarato' in f ? masBaratoPositivo(f.precioMasBarato) : null),
    producto: f.name || null,
  };
}

// ── La cuenta ──────────────────────────────────────────────────────────────

export type Sentido = 'SUBE' | 'BAJA' | null;

export interface PasoDeLaCuenta {
  clave: string;
  /** El nombre de la regla, sin comillas. null en el paso de la estrategia. */
  regla: string | null;
  /** Su id: la pantalla pone al lado el alcance (`alcancePorRegla`). null sin regla. */
  ruleId: string | null;
  /** «la que probás», «esta regla» o «encendida» (sólo en «¿Y si…?»). */
  marca: string | null;
  texto: string;
  /** Lo que va en gris después de un «·»: de dónde sale lo que pide. */
  detalle: string | null;
  manda: boolean;
  valor: string;
  sentido: Sentido;
}

export interface CuentaDelPrecio {
  pasos: PasoDeLaCuenta[];
  /**
   * La fila en negrita. En «¿Y si…?», cuánto cambia contra tus reglas de hoy;
   * sin precio posible, el mínimo y el máximo que no se tocan (maqueta v2). null
   * sólo sin cálculo. `salida`: la que se eligió, para que la pantalla la nombre
   * con el título de Por decidir (`tituloDeSalida`).
   */
  final: { texto: string; detalle: string; valor: string; salida: ClaveDeSalida | null } | null;
  notas: string[];
}

/**
 * «iguala al más barato (499,03)», «el más barato 2.549 − 3 %», «costo 7.084,35
 * ÷ (1 − 50 %)», «lista 809 − 10 %». El nombre de la tienda más barata llega con
 * el mercado (pieza 2): hasta entonces no se nombra.
 */
export function comoEnTexto(c: ComoPide): string {
  switch (c.metodo) {
    case 'MATCH_LOWEST':
      return `iguala al más barato (${fmt(c.masBarato)})`;
    case 'UNDERCUT_PERCENT':
      return `el más barato ${fmt(c.masBarato)} − ${pct(c.porcentaje)}`;
    case 'MARGIN_TARGET':
      return `costo ${fmt(c.costo)} ÷ (1 − ${pct(c.margen)})`;
    case 'DISCOUNT_PERCENT':
      return `lista ${fmt(c.lista)} − ${pct(c.porcentaje)}`;
  }
}

function parametroDe(p: PedidoDelRecorrido): string | null {
  const c = p.como;
  const n =
    c?.metodo === 'MARGIN_TARGET'
      ? c.margen
      : c?.metodo === 'UNDERCUT_PERCENT' || c?.metodo === 'DISCOUNT_PERCENT'
        ? c.porcentaje
        : null;
  if (p.usaMargenPropio)
    return n != null && c?.metodo === 'MARGIN_TARGET'
      ? `${pct(n)}, el margen propio del producto`
      : 'con el margen propio del producto';
  return n != null ? pct(n) : null;
}

function pasoDelPedido(p: PedidoDelRecorrido): PasoDeLaCuenta {
  const base = {
    clave: 'pide',
    regla: null,
    ruleId: null,
    marca: null,
    manda: false,
    sentido: null,
  } as const;
  if (p.origen === 'HOY')
    return {
      ...base,
      texto: 'Sin estrategia: se parte del precio de hoy',
      detalle: null,
      valor: fmt(p.precio),
    };
  if (p.origen === 'VUELTA')
    return {
      ...base,
      texto: 'Vuelve a su precio de antes',
      detalle: p.explicacionDeVuelta,
      valor: fmt(p.precio),
    };
  const param = parametroDe(p);
  return {
    ...base,
    texto: `La estrategia${p.estrategia ? ` «${p.estrategia}»` : ''}${param ? ` (${param})` : ''} pide`,
    detalle: p.como ? comoEnTexto(p.como) : null,
    valor: fmt(p.precio),
  };
}

function flecha(desde: number | null, hasta: number): { valor: string; sentido: Sentido } {
  if (desde == null || Math.abs(hasta - desde) < CASI) return { valor: fmt(hasta), sentido: null };
  return hasta > desde
    ? { valor: `↑ ${fmt(hasta)}`, sentido: 'SUBE' }
    : { valor: `↓ ${fmt(hasta)}`, sentido: 'BAJA' };
}

function queDejaLaRegla(r: RangoDeRegla): string {
  if (r.aviso != null) return `no se pudo medir: ${r.aviso}`;
  if (r.min != null && r.max != null)
    return `deja de ${pisoEnTexto(r.min)} a ${techoEnTexto(r.max)}`;
  if (r.min != null) return `no deja bajar de ${pisoEnTexto(r.min)}`;
  if (r.max != null) return `no deja pasar de ${techoEnTexto(r.max)}`;
  return 'no le pone límite';
}

/** Sin precio posible: lo que exige cada regla, con ≥ y ≤. */
function exigeLaRegla(r: RangoDeRegla): { valor: string; sentido: Sentido } {
  if (r.aviso != null) return { valor: '—', sentido: null };
  if (r.min != null && r.max != null)
    return { valor: `${pisoEnTexto(r.min)} – ${techoEnTexto(r.max)}`, sentido: null };
  if (r.min != null) return { valor: `≥ ${pisoEnTexto(r.min)}`, sentido: 'SUBE' };
  if (r.max != null) return { valor: `≤ ${techoEnTexto(r.max)}`, sentido: 'BAJA' };
  return { valor: 'se cumple', sentido: null };
}

/** «lo termina en ,99», «lo redondea a 619». */
export function terminacion(hasta: number): string {
  const centavos = Math.round(hasta * 100) % 100;
  return centavos === 0
    ? `lo redondea a ${fmt(hasta)}`
    : `lo termina en ,${String(centavos).padStart(2, '0')}`;
}

function marcaDe(r: RecorridoDelPrecio, probando: boolean): string | null {
  if (!r.comparacion) return null;
  return probando ? r.comparacion.etiqueta : 'encendida';
}

/** «¿Y si…?»: «sin la regla 3.242,99 → con la regla +203,00», o «igual que sin la regla». */
function detalleDeLaPrueba(sin: number | null, con: number): string {
  if (sin == null) return 'sin la regla: no se sabe';
  const d = redondo(con - sin);
  if (Math.abs(d) < CASI) return 'igual que sin la regla';
  return `sin la regla ${fmt(sin)} → con la regla ${d > 0 ? '+' : '−'}${fmt(Math.abs(d))}`;
}

/** «hoy 809 → baja 190,01 · margen 15,05 %»; en el historial, «antes 809 → …». */
function detalleFinal(r: RecorridoDelPrecio, queda: number, tiempo: Tiempo): string {
  const partes: string[] = [];
  if (r.hoy != null) {
    const d = redondo(queda - r.hoy);
    partes.push(
      `${REFERENCIA[tiempo]} ${fmt(r.hoy)} → ${Math.abs(d) < CASI ? 'no cambia' : `${d > 0 ? 'sube' : 'baja'} ${fmt(Math.abs(d))}`}`
    );
  }
  const m = margenDe(queda, r.costo);
  partes.push(m == null ? 'margen — (sin costo cargado)' : `margen ${pct(m)}`);
  if (r.seSirve != null) partes.push(`mientras nadie decide se sirve ${fmt(r.seSirve)}`);
  return partes.join(' · ');
}

/**
 * La cuenta (spec 225, D2): lo que pide la estrategia → cada regla de tramo (y
 * cuál manda) → el redondeo si movió → dónde queda. Todo en palabras.
 */
export function cuentaDelPrecio(r: RecorridoDelPrecio, tiempo: Tiempo): CuentaDelPrecio {
  if (r.estado === 'SIN_CALCULO') return { pasos: [], final: null, notas: r.notas };
  const pasos: PasoDeLaCuenta[] = [];
  if (r.pedido) pasos.push(pasoDelPedido(r.pedido));
  const pide = r.pedido?.precio ?? null;
  const sinPrecio = r.estado === 'SIN_PRECIO_POSIBLE';
  // Lo que dejan las reglas, antes de redondear: ahí queda la que manda.
  const despuesDeLasReglas = r.redondeo ? r.redondeo.desde : r.queda;

  if (r.reglas) {
    r.reglas.forEach((regla, i) => {
      const cuanto = sinPrecio
        ? exigeLaRegla(regla)
        : regla.manda && despuesDeLasReglas != null
          ? flecha(pide, despuesDeLasReglas)
          : { valor: regla.aviso != null ? '—' : 'se cumple', sentido: null };
      pasos.push({
        clave: `${regla.ruleId}-${i}`,
        regla: regla.ruleName,
        ruleId: regla.ruleId,
        marca: marcaDe(r, regla.probando),
        texto: queDejaLaRegla(regla),
        detalle: null,
        manda: !sinPrecio && regla.manda,
        valor: cuanto.valor,
        sentido: cuanto.sentido,
      });
    });
    // Lote B2: las que alcanzan y no rigen, después de las que rigen, con quién decide.
    (r.pisadas ?? []).forEach((p, i) => {
      pasos.push({
        clave: `pisada-${p.ruleId}-${i}`,
        regla: p.ruleName,
        ruleId: p.ruleId,
        marca: null,
        texto: noRige(p),
        detalle: null,
        manda: false,
        valor: '—',
        sentido: null,
      });
    });
  } else if (r.reglaQueLimito && r.queda != null) {
    pasos.push({
      clave: 'limito',
      regla: r.reglaQueLimito,
      ruleId: null,
      marca: null,
      texto: 'lo limitó',
      detalle: null,
      manda: true,
      ...flecha(pide, r.queda),
    });
  }

  if (r.redondeo && !sinPrecio) {
    const { desde, hasta } = r.redondeo;
    const sinPasarse =
      hasta < desde - CASI && r.cruce?.max != null && Math.abs(desde - r.cruce.max) < 0.01;
    pasos.push({
      clave: 'redondeo',
      regla: r.redondeo.ruleName,
      ruleId: r.redondeo.ruleId,
      marca: marcaDe(r, r.redondeo.probando),
      texto: `${terminacion(hasta)}${sinPasarse ? ' sin pasarse' : ''}`,
      detalle: null,
      manda: false,
      ...flecha(desde, hasta),
    });
  }

  // Con una salida elegida, la fila dice el precio y nada del redondeo: el
  // retrato no guarda cuál redondeó la salida (ausente = no sé).
  const final =
    r.estado === 'CALCULADO' && r.queda != null && !r.comparacion
      ? {
          texto: VERBOS[tiempo],
          detalle: detalleFinal(r, r.queda, tiempo),
          valor: fmt(r.queda),
          salida: null,
        }
      : r.estado === 'CALCULADO' && r.queda != null && r.comparacion
        ? {
            texto: VERBOS.SI_CORRIERA,
            detalle: detalleDeLaPrueba(r.comparacion.sin, r.queda),
            valor: fmt(r.queda),
            salida: null,
          }
        : sinPrecio && r.elegida
          ? {
              texto: VERBOS[tiempo],
              detalle: detalleFinal(r, r.elegida.precio, tiempo),
              valor: fmt(r.elegida.precio),
              salida: r.elegida.clave,
            }
          : sinPrecio
            ? {
                texto: 'Conflicto entre reglas',
                detalle:
                  r.cruce?.min != null && r.cruce.max != null
                    ? `el mínimo (${pisoEnTexto(r.cruce.min)}) queda por encima del máximo (${techoEnTexto(r.cruce.max)})`
                    : 'tus reglas no se pueden cumplir todas a la vez',
                valor: '—',
                salida: null,
              }
            : null;
  return { pasos, final, notas: r.notas };
}
